import React from "react";
import {
  Avatar,
  Box,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import LightModeIcon from "@mui/icons-material/LightMode";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TranslateIcon from "@mui/icons-material/Translate";
import { alpha } from "@mui/material/styles";
import { AnimatePresence, motion } from "framer-motion";
import { APP_HEADER_LOGO_TEXT, APP_PUBLIC_URL } from "../config";
import { SUPPORTED_LOCALES, t } from "../i18n";
import { uiMotion } from "../uiStyles";
import BrandLogo from "./BrandLogo";

export default function SkeletonPageHeader({
  onToggleTheme,
  onOpenAccount,
  onOpenAdminPanel,
  onOpenDocs,
  onSignOut,
  onLocaleChange,
  currentLocale = "ru",
  currentTheme = "light",
  canManage = false,
  showLanguageSwitcher = true,
  managementLabel = "Панель управления",
  accountName = "U",
  accountAvatarUrl = "",
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = React.useState(null);

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1200,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        py: 2,
        px: { xs: 1.5, sm: 2, md: 3 },
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.86),
        backdropFilter: "saturate(1.4) blur(12px)",
        transition: "background-color 0.3s ease",
      }}
    >
      <a
        href={APP_PUBLIC_URL}
        style={{ textDecoration: "none", flexShrink: 0 }}
      >
        <BrandLogo text={APP_HEADER_LOGO_TEXT} theme={currentTheme} fontSize={{ xs: 24, md: 30 }} />
      </a>

      <Box
        sx={{
          display: "flex",
          gap: { xs: 0.25, sm: 0.5, md: 1 },
          flexShrink: 0,
          alignItems: "center",
        }}
      >
        {showLanguageSwitcher && (
          <Tooltip title={t("common.language", currentLocale)} arrow>
            <IconButton
              onClick={(event) => setAnchorEl(event.currentTarget)}
              sx={{ color: "text.primary" }}
            >
              <TranslateIcon />
            </IconButton>
          </Tooltip>
        )}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          {SUPPORTED_LOCALES.map((locale) => (
            <MenuItem
              key={locale}
              selected={locale === currentLocale}
              onClick={() => {
                onLocaleChange?.(locale);
                setAnchorEl(null);
              }}
            >
              {t(`language.${locale}`, currentLocale)}
            </MenuItem>
          ))}
        </Menu>
        {onOpenDocs && (
          <Tooltip title={t("shell.help", currentLocale)} arrow>
            <IconButton
              onClick={onOpenDocs}
              sx={{ color: "text.primary" }}
            >
              <HelpOutlineRoundedIcon />
            </IconButton>
          </Tooltip>
        )}
        {canManage && (
          <Tooltip
            title={managementLabel}
            arrow
          >
            <IconButton
              onClick={() => onOpenAdminPanel?.()}
              sx={{ color: "text.primary" }}
            >
              <SettingsOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t("shell.changeTheme", currentLocale)} arrow>
          <IconButton onClick={onToggleTheme} sx={{ color: "text.primary" }}>
            <AnimatePresence mode="wait" initial={false}>
              <Box
                key={currentTheme}
                component={motion.span}
                initial={{ opacity: 0, rotate: -90, scale: 0.6 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: 90, scale: 0.6 }}
                transition={{ duration: uiMotion.fast, ease: uiMotion.ease }}
                sx={{ display: "inline-flex" }}
              >
                {currentTheme === "dark" ? <LightModeIcon /> : <DarkModeOutlinedIcon />}
              </Box>
            </AnimatePresence>
          </IconButton>
        </Tooltip>
        <Tooltip title={t("shell.accountMenu", currentLocale)} arrow>
          <IconButton
            onClick={(event) => setProfileAnchorEl(event.currentTarget)}
            sx={{ p: 0.35 }}
          >
            <Avatar
              src={accountAvatarUrl || ""}
              sx={{
                width: 34,
                height: 34,
                bgcolor: "ui.avatar",
                color: "#fff",
                fontSize: 15,
                fontWeight: 600,
              }}
            >
              {accountName}
            </Avatar>
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={profileAnchorEl}
          open={Boolean(profileAnchorEl)}
          onClose={() => setProfileAnchorEl(null)}
        >
          <MenuItem
            onClick={() => {
              onOpenAccount?.();
              setProfileAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <PersonOutlineIcon fontSize="small" />
            </ListItemIcon>
            {t("shell.account", currentLocale)}
          </MenuItem>
          <MenuItem
            onClick={() => {
              onSignOut?.();
              setProfileAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            {t("shell.signOut", currentLocale)}
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
